import React, { useState, useEffect } from "react";
import { postRequestAuthenticated } from "../../../globals/imports.js";
import { toast } from "../../../utils/toast";

const THEME_MODES = ["light", "dark", "auto"];
const AUTH_MODES = [
  { key: "otp", label: "Email OTP", desc: "Members sign in with a one-time code." },
  { key: "google", label: "Google", desc: "Members sign in with Google." },
  { key: "jwt", label: "JWT SSO", desc: "Your app signs a token; members are auto-logged in." },
  { key: "trusted", label: "Trusted", desc: "No verification — members you pass in are trusted." },
];
// Feature toggles the embed actually reads. The admin owns this config; the
// embed is just a renderer (no in-embed configuration controls).
const FEATURES = [
  { key: "channelSwitcher", label: "Channel switcher", desc: "Let members open your other channels from the header dropdown." },
  { key: "dms", label: "Direct messages", desc: "Members can message each other from the embed." },
  { key: "talkToBrand", label: "Talk to the brand", desc: 'Show a "Talk to us" button so members can message your team.' },
  { key: "memberDirectory", label: "View members", desc: "Members can see the community member list." },
  { key: "events", label: "Events", desc: "Hosts can create events inside topics." },
  { key: "polls", label: "Polls", desc: "Hosts can create polls in the chat." },
  { key: "fileSharing", label: "File sharing", desc: "Members can attach files in chat." },
];
const defaultFeatures = () =>
  FEATURES.reduce((acc, f) => ({ ...acc, [f.key]: true }), {});

// Onboarding step: configure the embed (branding + how members sign in). Reuses
// the EmbedConfig endpoints (/embed/config, /embed/config/update).
const CustomizeStep = ({ onDone }) => {
  const [branding, setBranding] = useState({
    displayName: "",
    accentColor: "",
    accentTextColor: "",
    themeMode: "light",
  });
  const [authMode, setAuthMode] = useState("otp");
  const [features, setFeatures] = useState(defaultFeatures());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    postRequestAuthenticated("/embed/config", {})
      .then((res) => {
        if (active && res?.success && res.config) {
          const b = res.config.branding || {};
          setBranding({
            displayName: b.displayName || "",
            accentColor: b.accentColor || "",
            accentTextColor: b.accentTextColor || "",
            themeMode: b.themeMode || "light",
          });
          if (res.config.auth?.mode) setAuthMode(res.config.auth.mode);
          if (res.config.features) {
            const cf = res.config.features;
            setFeatures(
              FEATURES.reduce(
                (acc, f) => ({ ...acc, [f.key]: cf[f.key] !== false }),
                {}
              )
            );
          }
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const save = async (proceed) => {
    setSaving(true);
    try {
      const res = await postRequestAuthenticated("/embed/config/update", {
        branding,
        auth: { mode: authMode },
        features,
      });
      // Backend may reject (e.g. JWT/SSO is Growth-only) — surface it and stop.
      if (res && res.success === false) {
        toast(res.message || "Couldn't save your settings.", "error");
        return;
      }
      // Finishing the wizard is one-time — mark onboarding complete so
      // /admin/setup redirects to the dashboard from now on.
      if (proceed) {
        await postRequestAuthenticated("/setup/complete", {});
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      if (proceed && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-theme-secondaryText">
          Customize your community
        </h2>
        <p className="mt-1 text-sm text-theme-placeholder">
          Brand the embed and choose how your members sign in. You can change
          all of this later in Settings.
        </p>
      </div>

      {/* Branding */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
            Display name
          </label>
          <input
            value={branding.displayName}
            onChange={(e) =>
              setBranding((b) => ({ ...b, displayName: e.target.value }))
            }
            placeholder="Acme Community"
            className="w-full rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
            Accent color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.accentColor || "#2563eb"}
              onChange={(e) =>
                setBranding((b) => ({ ...b, accentColor: e.target.value }))
              }
              className="h-10 w-12 cursor-pointer rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground"
            />
            <input
              value={branding.accentColor}
              onChange={(e) =>
                setBranding((b) => ({ ...b, accentColor: e.target.value }))
              }
              placeholder="#2563eb"
              className="w-full rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
            />
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
            Text on accent
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={branding.accentTextColor || "#ffffff"}
              onChange={(e) =>
                setBranding((b) => ({ ...b, accentTextColor: e.target.value }))
              }
              className="h-10 w-12 cursor-pointer rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground"
            />
            <input
              value={branding.accentTextColor}
              onChange={(e) =>
                setBranding((b) => ({ ...b, accentTextColor: e.target.value }))
              }
              placeholder="#ffffff"
              className="w-full rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
            />
          </div>
          <p className="mt-1 text-[11px] text-theme-placeholder">
            Text &amp; icons shown on top of the accent (e.g. button labels).
          </p>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
            Theme
          </label>
          <div className="inline-flex rounded-lg border border-theme-chatDivider p-1">
            {THEME_MODES.map((m) => (
              <button
                key={m}
                onClick={() => setBranding((b) => ({ ...b, themeMode: m }))}
                className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                  branding.themeMode === m
                    ? "bg-theme-accent text-theme-accentText"
                    : "text-theme-primaryText hover:bg-theme-tertiaryBackground"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Auth */}
      <div className="mt-6">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          How members sign in
        </label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {AUTH_MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => setAuthMode(m.key)}
              className={`flex flex-col items-start rounded-xl border p-3 text-left transition-colors ${
                authMode === m.key
                  ? "border-theme-accent bg-theme-accentSoft"
                  : "border-theme-chatDivider hover:bg-theme-tertiaryBackground"
              }`}
            >
              <span className="flex items-center gap-1.5 text-sm font-medium text-theme-secondaryText">
                {m.label}
                {m.key === "jwt" && (
                  <span className="rounded-full bg-theme-accentSoft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-theme-accent">
                    Growth
                  </span>
                )}
              </span>
              <span className="text-xs text-theme-placeholder">{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Features — what the embed shows. The admin owns this; the embed reads it. */}
      <div className="mt-6">
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          Features
        </label>
        <div className="flex flex-col gap-2">
          {FEATURES.map((f) => {
            const on = features[f.key] !== false;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() =>
                  setFeatures((prev) => ({ ...prev, [f.key]: !on }))
                }
                className="flex w-full items-center justify-between rounded-xl border border-theme-chatDivider p-3 text-left transition-colors hover:bg-theme-tertiaryBackground"
              >
                <span className="pr-4">
                  <span className="block text-sm font-medium text-theme-secondaryText">
                    {f.label}
                  </span>
                  <span className="block text-xs text-theme-placeholder">
                    {f.desc}
                  </span>
                </span>
                <span
                  className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                    on ? "bg-theme-accent" : "bg-theme-chatDivider"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      on ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-theme-placeholder">
          The defaults work fine — you can change branding, sign-in and features
          anytime in Settings.
        </p>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-theme-success">Saved ✓</span>}
          <button
            onClick={() => save(true)}
            disabled={saving}
            className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-semibold text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save & finish →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomizeStep;
